// ============================================================================
// demoData.ts — the PRECOMPUTED curated-gallery dataset for the slider demo.
// ============================================================================
//
//   ⚠️  PLACEHOLDER DATASET — NO REAL IMAGERY  ⚠️
//
// HARD GUARDRAIL (PRD §1.9-B): this demo is a CURATED-gallery slider playground
// ONLY. There are NO arbitrary user uploads (an ungated "classify any image"
// surface is a CSAM/abuse magnet and is explicitly NOT shipped). And we never
// source, generate, or embed pornographic/nude/explicit imagery anywhere in
// this repo. So every tile below is an ABSTRACT placeholder:
//   • the thumbnail is a solid/gradient SVG swatch (no photo), and
//   • the content is described by a TEXT label + TEXT band only.
//
// >>> PLUG-IN POINT FOR MIKE <<<
// Replace these placeholder tiles with the real curated, legally-sourced /
// synthetic gallery spanning swimsuit → suggestive → explicit. Keep the SAME
// DemoTile shape. For each REAL image, run it once through
// kinsafe_local/pipeline.py and capture the ACTUAL `parts` / `actions` /
// `score` / `isExplicit` and three describer-prompt outputs — drop those in
// here. The live gating (gating.ts) then reflects the real product exactly.
//
// The placeholder signals below are SYNTHETIC but chosen to demonstrate the
// gating logic faithfully (mirroring gate_by_sensitivity semantics):
//   • lower sensitivity (the "catch more" end) catches the borderline tiles;
//   • the body-part axis only ever TIGHTENS (genital-class at strict);
//   • a high gatekeeper score trips the both-axes-off safety net.
// ============================================================================

import type { DemoTile } from "./types";

// Abstract placeholder swatches — purely decorative CSS gradients. No images.
const SWATCH = {
  calm: "linear-gradient(135deg, #cfe3d8 0%, #a9cbb8 100%)",
  sand: "linear-gradient(135deg, #efe2c4 0%, #e3c79a 100%)",
  amber: "linear-gradient(135deg, #f0d3a8 0%, #e0a96d 100%)",
  rose: "linear-gradient(135deg, #efc7c0 0%, #d99a90 100%)",
  clay: "linear-gradient(135deg, #e6b8a8 0%, #cd8a73 100%)",
  ember: "linear-gradient(135deg, #e4a892 0%, #c2705a 100%)",
} as const;

export const DEMO_TILES: DemoTile[] = [
  // ---- SAFE: never flags on any setting (no parts, no acts, low score) -----
  {
    id: "tile-landscape",
    label: "Mountain landscape",
    band: "safe",
    swatch: SWATCH.calm,
    parts: [],
    actions: [],
    score: 0.02,
    isExplicit: false,
    scenes: {
      explicit: "A wide mountain valley at golden hour. No people are present.",
      clinical: "A landscape photograph of mountains and a valley. No people.",
      tame: "Nothing of concern — a scenic landscape.",
    },
  },
  {
    id: "tile-family",
    label: "Clothed family photo",
    band: "safe",
    swatch: SWATCH.calm,
    parts: [],
    actions: [],
    score: 0.08,
    isExplicit: false,
    scenes: {
      explicit:
        "Two fully-clothed adults stand together outdoors, both dressed in everyday clothing.",
      clinical: "Two clothed adults photographed outdoors. No exposure.",
      tame: "Nothing of concern — people in ordinary clothing.",
    },
  },

  // ---- BEACHWEAR: borderline. Only the "catch more" (lenient / high-sens)
  //      end flags it — there is no named part/act and the score is moderate,
  //      so it relies on the fallback-nudity language. Demonstrates the honest
  //      v1 limitation: there is no dedicated "suggestive-but-clothed" model.
  {
    id: "tile-swimsuit",
    label: "Beachwear — swimsuit at the beach",
    band: "beachwear",
    swatch: SWATCH.sand,
    parts: [],
    actions: [],
    // is_explicit true with NO named part/act == the fallback-nudity case the
    // lenient end of each axis honors.
    score: 0.46,
    isExplicit: true,
    scenes: {
      explicit:
        "An adult in a two-piece swimsuit standing on a beach; no anatomy is exposed.",
      clinical:
        "An adult wearing beach swimwear. No specific body parts are exposed.",
      tame: "Possibly suggestive — swimwear, nothing explicit.",
    },
  },
  {
    id: "tile-underwear",
    label: "Suggestive pose — underwear",
    band: "suggestive",
    swatch: SWATCH.amber,
    parts: [],
    actions: [],
    score: 0.58,
    isExplicit: true,
    scenes: {
      explicit:
        "An adult in underwear in a suggestive pose; no genitals or breasts are exposed.",
      clinical: "An adult in underwear, posing. No explicit anatomy visible.",
      tame: "Possibly suggestive content.",
    },
  },

  // ---- SUGGESTIVE→EXPLICIT: a named body part (breasts), no act. The body-
  //      part axis catches this at standard; strict (genital-class only) does
  //      NOT, since breasts aren't genital-class. Shows the body-part tighten.
  {
    id: "tile-topless",
    label: "Partial nudity — exposed breasts",
    band: "explicit",
    swatch: SWATCH.rose,
    parts: ["breasts", "nipples"],
    actions: [],
    score: 0.86,
    isExplicit: true,
    scenes: {
      explicit: "An adult woman is topless, with exposed breasts and nipples.",
      clinical: "An adult female with exposed breasts and visible nipples.",
      tame: "Explicit adult content is shown.",
    },
  },

  // ---- EXPLICIT: a named act + genital-class part + high score. Flags on
  //      essentially every non-off setting, including strict on both axes, and
  //      trips the high-score safety net even with both axes off.
  {
    id: "tile-explicit-act",
    label: "Explicit — sexual act, exposed genitals",
    band: "explicit",
    swatch: SWATCH.ember,
    parts: ["vagina/vulva", "penis/testicles"],
    actions: ["sexual intercourse"],
    score: 0.97,
    isExplicit: true,
    scenes: {
      explicit:
        "Two adults are engaged in sexual intercourse with exposed genitals.",
      clinical:
        "Two adults engaged in penetrative intercourse; genitals are visible.",
      tame: "Explicit adult content is shown.",
    },
  },
];
